import { useCallback, useContext, useEffect, useState } from "preact/hooks";
import { ComponentChildren, createContext } from "preact";

import { ApiError } from "@api/base/api-caller";
import { createRole } from "@api/role-management";
import { usePopup } from "@contexts/popup";
import { transformManagementEvent } from "@transformers/management";
import { trackError } from "@utils/analytics";

interface Meta {
  ready: boolean;
  create: (name: string) => Promise<void>;
}

interface ProviderProps {
  children: ComponentChildren;
}

const Context = createContext<Meta | null>(null);

export function RoleManagementProvider({ children }: ProviderProps) {
  const { alert } = usePopup();
  const [ready, setReady] = useState(false);

  const create = useCallback(async (name: string) => {
    setReady(false);

    try {
      const data = transformManagementEvent(
        await createRole(name)
      );

      if (!data.success) {
        alert(
          data.reason || "Something went wrong!",
          "error",
        );
      } else {
        alert(
          data.reason || "Action successful!",
          "success",
          "Role Created",
        )
      }
    } catch (error: ApiError | any | unknown) {
      trackError(error);

      alert(
        (error.data as any)?.detail?.message || "Something went wrong!",
        "error",
      );
    }

    setReady(true);
  }, []);

  const load = useCallback(async () => {
    setReady(true);
  }, []);

  useEffect(() => {
    load();
  }, []);

  const value = {
    ready,
    create,
  };

  return <Context.Provider value={value}>
    {children}
  </Context.Provider>
}

export function useRoleManagement(): Meta {
  return useContext(Context)!;
}

